import { useState } from "react";
import { toast } from "react-toastify";
import { useLazyQuery } from "@apollo/client";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { forgotPassword } from "../../gql/queries/users";
import CustomTextField from "../forms/theme-elements/CustomTextField";
import CustomFormLabel from "../forms/theme-elements/CustomFormLabel";
import { isValidEmail } from "../../utils/validations";
import { checkEmail } from "../../gql/mutations/auth";

const LoaderStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "& svg": {
    color: "#fff",
  },
};

function AuthForgotPassword({ routeLocation }) {
  const [forgetPassword] = useLazyQuery(forgotPassword, { fetchPolicy: "network-only" });
  const [checkEmailQuery] = useLazyQuery(checkEmail, { fetchPolicy: "network-only" });

  const [isLoading, setIsLoading] = useState(false);
  const [errormsg, setErrorMsg] = useState("");
  const [userName, setUserName] = useState(null);
  const [values, setValues] = useState({
    email: routeLocation.state?.email || "",
    message: "",
  });

  const { email } = values;

  const debouncedCheckEmail = debounce(async newEmail => {
    if (isValidEmail(newEmail)) {
      try {
        await checkEmailQuery({
          variables: { email: newEmail },
          onCompleted: ({ checkEmail: userDetails }) => {
            setUserName(userDetails.name);
          },
          onError: () => {
            setErrorMsg("No user found for this email");
          },
        });
      } catch (err) {
        console.log("GraphQL Error: ", err);
      }
    }
  }, 800);

  const handleChange = e => {
    const newEmail = e.target.value;
    setValues({ ...values, message: "", email: newEmail });

    if (newEmail === "") {
      setErrorMsg("Email is required.");
    } else if (!isValidEmail(newEmail)) {
      setErrorMsg("Please enter a valid email address.");
    } else {
      setErrorMsg("");
      debouncedCheckEmail(newEmail);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setErrorMsg("");

    if (email === "") {
      setErrorMsg("Email is required. This field cannot be left empty.");
      return;
    }

    if (!isValidEmail(email)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    await forgetPassword({
      variables: { email },
      onCompleted: ({ preForgetPassword }) => {
        toast.success(preForgetPassword.message);
        setValues({ email: "", message: "" });
      },
      onError: err => {
        console.log(err.message);
        setErrorMsg(err.message);
      },
    });
    setIsLoading(false);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && (isLoading || !!errormsg)) {
      e.preventDefault();
    } else if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  return (
    <Stack mt={4} spacing={2} sx={{ "& .MuiFormHelperText-root": { color: "#ff0000 !important" } }}>
      <CustomFormLabel htmlFor="reset-email">Email Address</CustomFormLabel>
      <CustomTextField
        id="reset-email"
        variant="outlined"
        fullWidth
        type="email"
        value={email}
        error={!!errormsg}
        helperText={errormsg}
        required
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        sx={{
          "& input:is(:autofill)": {
            boxShadow: "0 0 0 100px #e8f0fe inset",
            WebkitTextFillColor: "#000000",
            caretColor: "#000000",
            "& + fieldset": { borderColor: "#DFE5EF" },
          },
        }}
      />
      {userName && <Typography>User Name :- {userName}</Typography>}

      <Button
        sx={{
          backgroundColor: "#5D87FF",
          color: "#fff",
          transition: "0.3s all",
          "&:hover": {
            opacity: "0.9",
            backgroundColor: "#5D87FF",
          },
          "&.Mui-disabled": {
            opacity: "0.4",
            color: "#c6c6c6",
          },
        }}
        size="large"
        fullWidth
        disabled={isLoading || !!errormsg || email.trim() === ""}
        onClick={handleSubmit}
      >
        {isLoading ? <CircularProgress size={30} sx={LoaderStyle} /> : "Send password reset link"}
      </Button>

      <Button
        sx={{
          color: "#5D87FF",
          backgroundColor: "transparent",
          border: "2px solid #5D87FF",
          "&:hover": {
            backgroundColor: "#5D87FF",
          },
        }}
        size="large"
        fullWidth
        component={Link}
        to="/"
      >
        Back to Login
      </Button>
    </Stack>
  );
}

export default AuthForgotPassword;
